import React, { useEffect, useMemo, useState } from "react";
import LoadingSpinner from '../../ui/LoadingSpinner';
import ChartBase from '../ChartBase';
import { donutChartData } from '../data';
import { useActiveSitesStats } from '../../../hooks/useActiveSitesStats';
import type { ActiveSitesStats } from '../../../hooks/useActiveSitesStats';
import type { ApexOptions } from 'apexcharts';

const NO_DATA_DELAY_MS = 700;

function buildDonutData(stats: ActiveSitesStats | null) {
    const counts = stats?.categoryCounts ?? {};
    const activeNotAck = counts['active-not-ack'] ?? 0;
    const activeAck = counts['active-ack'] ?? 0;
    const inactiveNotAck = counts['not-active-not-ack'] ?? 0;

    const labels = ['Active - Unacknowledged', 'Active - Acknowledged', 'Inactive - Unacknowledged'];
    const series = [activeNotAck, activeAck, inactiveNotAck];
    return { labels, series };
}

const AlarmSplitChart: React.FC = () => {
    // Use authoritative stats so donut matches the cards
    const { stats, loading } = useActiveSitesStats();
    const { labels, series } = useMemo(() => buildDonutData(stats), [stats]);

    const chartOptions: ApexOptions = {
        ...donutChartData.options,
        labels,
        chart: {
            ...donutChartData.options.chart,
            id: "donutChart"
        },
        tooltip: {
            ...(donutChartData.options.tooltip ?? {}),
            theme: 'dark',
        },
    };

    // Debounce No Data display to avoid brief 'No Data' flashes.
    // Keep showing the spinner until either data appears or the timeout elapses.
    const [showNoData, setShowNoData] = useState(false);
    const total = series.reduce((s, v) => s + (Number(v) || 0), 0);
    const hasData = total > 0;
    useEffect(() => {
        if (loading || hasData) {
            setShowNoData(false);
            return undefined;
        }
        const t = setTimeout(() => setShowNoData(true), NO_DATA_DELAY_MS);
        return () => clearTimeout(t);
    }, [loading, hasData]);

    const chartContent = useMemo(() => {
        if (loading || (!showNoData && !hasData)) {
            return <LoadingSpinner message="Loading chart data…" />;
        }

        if (!hasData) {
            return <div className="table-responsive-nodata-text">No Data</div>;
        }

        return (
            <ChartBase
                series={series}
                options={chartOptions}
                type={'donut'}
                width={'100%'}
                height={422}
                id="donutChart"
            />
        );
    }, [loading, showNoData, hasData, series, chartOptions]);

    return (
        <div className="card pt-1">
            <div className="charts donut">
                <h3>Alarm Status Split</h3>
                <h5>Alarm split by status and acknowledgment</h5>
                {chartContent}
            </div>
        </div>
    );
};

export default AlarmSplitChart;
